import { IDesignerState, IModal } from '@/../typings';
import Spinner from '@/assets/spinner';
import { onAddToast, onOpenModal, onSetPreviewBtnStatus, onSetPublishBtnStatus } from '@/store/actions';
import { zwClient } from '@/sync/zapworks';
import { IAPIError, useGetPublishErrorHandler, useIs360ContentMissing, useIsImageTrackingMissing, useIsTargetImageAllowanceExceeded } from '@/utils';
import { ToastsData } from '@/utils/toasts-data';
import { isProjectProcessingVideos } from '@/utils/videoProcessing';
import * as Sentry from '@sentry/react';
import { sleep, UARPublishResponse, IFileType } from '@zappar/zw-api-client';
import React, { CSSProperties, memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionButton } from '../../buttons';
import { bundleProject } from '../utils';
import CSS from './PublishBtn.module.scss';

interface IParentProps {
    onBeforePublish?: () => unknown;
    returnBeforePublish?: boolean;
	onPublish?: (projectSizeInMb: number) => unknown;
	disabled?: boolean;
	text?: string;
    showSpinner?: boolean;
	style?: CSSProperties;
	videoProcessingWarning?: boolean;
}

export const checkPublishStatus = async (statusUrl: string) => {
	let isPublished = false;
	while (!isPublished) {
		const statusResp = await fetch(statusUrl);
		const respObj = await statusResp.json();
		if (respObj.status === 'success') {
			isPublished = true;
		} else {
			await sleep(500);
		}
	}
};

const PublishButton: React.FunctionComponent<IParentProps> = ({ onPublish, disabled, style, text, videoProcessingWarning, onBeforePublish, returnBeforePublish = false }) => {
	const dispatch = useDispatch();
    const publishBtnStatus = useSelector((state: IDesignerState) => state.userReducer.publishBtnStatus)
	const projectId = useSelector((state: IDesignerState) => state.userReducer.project.id);
	const trackingImageMissing = useIsImageTrackingMissing();
	const is360ContentMissing = useIs360ContentMissing();
	const cancelRef = useRef(false);
	const publishErrorHandler = useGetPublishErrorHandler();
    const targetImageAllowanceExceeded = useIsTargetImageAllowanceExceeded()
    const [isBusy, setIsBusy] = useState(false)

	useEffect(() => {
		() => cancelRef.current = true;
	}, [])

    disabled = disabled || publishBtnStatus === 'disabled' || targetImageAllowanceExceeded;

	return (
		<ActionButton
			className={`${CSS.PublishBtn} ${disabled ? `${CSS.AppearDisabled}` : ''}  ${publishBtnStatus === 'processing' ? CSS.Transparent : ''}`}
			style={style}
			onClick={async () => {
                if (isBusy) return;
                setIsBusy(true);

                if (targetImageAllowanceExceeded) {
                    dispatch(onAddToast(ToastsData.TargetImageAllowanceExceededError));
                    setIsBusy(false);
                    return;
                }
                
				if (disabled) {
                    if (publishBtnStatus === 'disabled') return; // TODO: this avoids showing a toast but this code needs a review
					let msg = ToastsData.PublishNoTrkImgError;
					if (!trackingImageMissing && is360ContentMissing) msg = ToastsData.PublishNo360ContentError;
					if (trackingImageMissing && is360ContentMissing) msg = ToastsData.PublishNoTrkImgAnd360ContentError
					dispatch(onAddToast(msg));
                    setIsBusy(false);
					return;
				}

                if (onBeforePublish) await onBeforePublish();

                if (returnBeforePublish) {
                    setIsBusy(false);
                    return
                }

                dispatch(onSetPublishBtnStatus('processing'));

				if (videoProcessingWarning) {
					const isProcessingVideos = await isProjectProcessingVideos();  
					if (isProcessingVideos) {
						dispatch(onOpenModal(IModal.videoProcessingWarning))
                        dispatch(onSetPublishBtnStatus('idle'))
                        setIsBusy(false);
						return;
					}
				}

				try {
					if (typeof projectId === 'undefined') {
                        setIsBusy(false);
                        throw 'Project ID is undefined'
                    }
					if (cancelRef.current) {
                        setIsBusy(false);
                        return
                    }
                    dispatch(onSetPreviewBtnStatus('disabled'));
                    const file = await bundleProject();
                    const [uploadRes, versions] = await Promise.all([
                        zwClient.zml.uploadFile(file, IFileType.UARPackage, () => { /**/ }),
                        zwClient.getProjectVersionsAll(projectId)
                    ])
					const version = `v${versions.length + 1}.0` // TODO: the version name format should be "v{number of publishes}.{number of previews}"
					const resp = await zwClient.publish<UARPublishResponse>(projectId, { version, packagekey: uploadRes.key });
                    dispatch(onSetPreviewBtnStatus('idle'));
                    await checkPublishStatus(resp.statusUrl as string);
					dispatch(onAddToast(ToastsData.PublishSuccess));
					onPublish?.(file.size / (1024 * 1024));
                    setIsBusy(false);
				} catch (err) {
					Sentry.captureException(err);
					publishErrorHandler(err as IAPIError, ToastsData.PublishError);
				}
                dispatch(onSetPublishBtnStatus('idle'))
                setIsBusy(false);
                window.clarity?.("event", "publish: button")
			}}
		>
			{(publishBtnStatus === 'processing' || isBusy) ? (
				<div className={CSS.LoadingPlaceholder}>
					<Spinner />
				</div>
			) : (
				text ?? 'Publish'
			)}
		</ActionButton>
	);
};

export default memo(PublishButton);
